import { useCallback, useEffect, useMemo, useState } from "react";
import { ENDPOINT } from "../services/api-endpoint";
import { getList } from "../services/services";
import { materialCategoryLabel } from "../constants/materialCategories";

export type MaterialCategoryOption = {
  id: number;
  name: string;
};

export type MaterialGradeOption = {
  id: number;
  name: string;
  categoryId?: number;
};

function parseCategoryRows(raw: unknown): MaterialCategoryOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const r = row as Record<string, unknown>;
      const id = Number(r.id);
      const name = String(r.name ?? "").trim();
      if (!Number.isFinite(id) || id <= 0 || !name) return null;
      return { id, name };
    })
    .filter((x): x is MaterialCategoryOption => x != null);
}

function parseGradeRows(raw: unknown): MaterialGradeOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const r = row as Record<string, unknown>;
      const id = Number(r.id);
      const name = String(r.name ?? "").trim();
      const categoryId = Number(r.categoryId ?? r.category_id);
      if (!Number.isFinite(id) || id <= 0 || !name) return null;
      return {
        id,
        name,
        ...(Number.isFinite(categoryId) && categoryId > 0 ? { categoryId } : {}),
      };
    })
    .filter((x): x is MaterialGradeOption => x != null);
}

/** Load Mongo-backed material categories and print_materials for a selected category. */
export function useMaterialCatalog(selectedCategoryId: string) {
  const [categories, setCategories] = useState<MaterialCategoryOption[]>([]);
  const [grades, setGrades] = useState<MaterialGradeOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setCatalogLoading(true);
      try {
        const rows = await getList(ENDPOINT.MATERIAL_CATALOG_CATEGORIES);
        if (!cancelled) setCategories(parseCategoryRows(rows));
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cid = Number(selectedCategoryId);
    if (!Number.isFinite(cid) || cid <= 0) {
      setGrades([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getList(ENDPOINT.MATERIAL_CATALOG_GRADES, { categoryId: cid });
        if (!cancelled) setGrades(parseGradeRows(rows));
      } catch {
        if (!cancelled) setGrades([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId]);

  const categoryById = useMemo(
    () => new Map(categories.map((c) => [String(c.id), c.name])),
    [categories],
  );
  const gradeById = useMemo(
    () => new Map(grades.map((g) => [String(g.id), g.name])),
    [grades],
  );

  const resolveCategoryLabel = useCallback(
    (value: string | null | undefined) => {
      const v = String(value ?? "").trim();
      if (!v) return "";
      return categoryById.get(v) ?? materialCategoryLabel(v);
    },
    [categoryById],
  );

  const resolveMaterialLabel = useCallback(
    (categoryValue: string | null | undefined, materialValue: string | null | undefined) => {
      const m = String(materialValue ?? "").trim();
      if (!m) return "";
      return gradeById.get(m) ?? m;
    },
    [gradeById],
  );

  return {
    categories,
    grades,
    catalogLoading,
    categoryById,
    gradeById,
    resolveCategoryLabel,
    resolveMaterialLabel,
  };
}
